/**
 * Last-used dispatch view. Coordinators bounce between client Services,
 * sidebar Roster, and the Schedule tab. The URL stays authoritative when
 * ?view= is present; server-readable mode cookies resolve bare board links.
 * Legacy local storage is retained only for home-care/mixed migration.
 */

export const ROSTER_DISPATCH_VIEW_PREF_KEY = 'roster-dispatch-view:v1';
export const ROSTER_DISPATCH_WORKSPACE_PREF_KEY = 'roster-dispatch-workspace:v1';

export type StoredDispatchWorkspace = {
  view?: string;
  period?: string;
  week?: string;
  date?: string;
  clientId?: string;
};

export const RESIDENTIAL_DISPATCH_WORKSPACE_PREF_KEY = 'roster-dispatch-workspace:residential:v2';

const EPHEMERAL_DISPATCH_KEYS = [
  'shift',
  'slotId',
  'drawer',
  'drawerTab',
  'returnView',
  'returnDate',
  'returnWeek',
  'actions',
] as const;

const RESTORABLE_VIEWS = new Set([
  'week',
  'day',
  'clients',
  'workers',
  'coverage',
  'services',
  'departments',
  'heatmap',
  'month',
]);

const ROSTER_PERIOD_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validRosterPeriodId(value: string | null): value is string {
  return Boolean(value && ROSTER_PERIOD_ID_RE.test(value));
}

export function isRestorableDispatchView(value: string | undefined | null): boolean {
  return Boolean(value && RESTORABLE_VIEWS.has(value));
}

export function readStoredDispatchView(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ROSTER_DISPATCH_VIEW_PREF_KEY);
    return isRestorableDispatchView(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredDispatchView(view: string): void {
  if (typeof window === 'undefined') return;
  if (!isRestorableDispatchView(view)) return;
  try {
    window.localStorage.setItem(ROSTER_DISPATCH_VIEW_PREF_KEY, view);
  } catch {
    // Private mode / quota — lose stickiness rather than throw.
  }
}

export function readStoredDispatchWorkspace(residential = false): StoredDispatchWorkspace | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(
      residential ? RESIDENTIAL_DISPATCH_WORKSPACE_PREF_KEY : ROSTER_DISPATCH_WORKSPACE_PREF_KEY,
    );
    if (raw) {
      const parsed = JSON.parse(raw) as StoredDispatchWorkspace;
      if (parsed && typeof parsed === 'object') return parsed;
    }
    if (residential) return null;
    const view = readStoredDispatchView();
    return view ? { view } : null;
  } catch {
    return null;
  }
}

export function writeStoredDispatchWorkspace(
  workspace: StoredDispatchWorkspace,
  residential = false,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      residential ? RESIDENTIAL_DISPATCH_WORKSPACE_PREF_KEY : ROSTER_DISPATCH_WORKSPACE_PREF_KEY,
      JSON.stringify(workspace),
    );
    if (workspace.view && !residential) writeStoredDispatchView(workspace.view);
  } catch {
    // Private mode / quota — lose stickiness rather than throw.
  }
}

/** Keep the coordinator on the same dispatch workspace when a tab is a bare /roster/dispatch. */
function baseRosterSectionDispatchHref(
  tabHref: string,
  currentPathname: string,
  currentSearch: string,
  _stored: StoredDispatchWorkspace | null,
): string {
  // An explicit destination query is already an authored route contract.
  if (tabHref.includes('?')) return tabHref;

  const currentParams = new URLSearchParams(
    currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch,
  );

  // The board calls the selected period `period`; approvals consumes the same
  // selection as `period_id`. Carry only that stable scope key across this
  // boundary — drawer/client/remedy filters are board-local state.
  if (
    tabHref === '/roster/timesheets' &&
    (currentPathname === '/roster/dispatch' || currentPathname.startsWith('/roster/dispatch/'))
  ) {
    const period = currentParams.get('period');
    return validRosterPeriodId(period)
      ? `/roster/timesheets?period_id=${encodeURIComponent(period)}`
      : tabHref;
  }

  if (tabHref !== '/roster/dispatch') return tabHref;
  if (currentPathname === '/roster/dispatch' || currentPathname.startsWith('/roster/dispatch/')) {
    const params = currentParams;
    for (const key of EPHEMERAL_DISPATCH_KEYS) params.delete(key);
    params.delete('view');
    const encoded = params.toString();
    return encoded ? `/roster/dispatch?${encoded}` : '/roster/dispatch';
  }
  const explicitPeriod =
    currentParams.get('period_id') ??
    currentParams.get('period') ??
    currentPathname.match(/^\/roster\/periods\/([^/]+)/)?.[1] ??
    null;
  return validRosterPeriodId(explicitPeriod)
    ? `/roster/dispatch?period=${encodeURIComponent(explicitPeriod)}`
    : tabHref;
}

/** Carry stable roster scope between task destinations, never drawer/action state. */
export function rosterSectionDispatchHref(
  tabHref: string,
  currentPathname: string,
  currentSearch: string,
  stored: StoredDispatchWorkspace | null,
): string {
  const attendance = tabHref === '/roster/dispatch?view=day';
  // Authored queries remain authoritative; attendance is a scoped view switch.
  if (tabHref.includes('?') && !attendance) return tabHref;
  const inRoster = (path: string) =>
    path === '/roster' ||
    path.startsWith('/roster/') ||
    path === '/workforce/availability' ||
    path.startsWith('/workforce/availability/') ||
    path === '/home-care/schedules' ||
    path.startsWith('/home-care/schedules/');
  if (!inRoster(tabHref.split('?')[0] ?? '') || !inRoster(currentPathname)) return tabHref;
  const current = new URLSearchParams(currentSearch);
  const base = attendance
    ? tabHref
    : baseRosterSectionDispatchHref(tabHref, currentPathname, currentSearch, stored);
  const [path = '', query] = base.split('?');
  const params = new URLSearchParams(query);
  const period =
    current.get('period_id') ??
    current.get('period') ??
    currentPathname.match(/^\/roster\/periods\/([^/]+)/)?.[1] ??
    null;
  if (validRosterPeriodId(period)) {
    params.set(path.startsWith('/roster/timesheets') ? 'period_id' : 'period', period);
  }
  for (const key of ['facility', 'workspace', 'lens', 'program'] as const) {
    const value = current.get(key);
    if (value) params.set(key, value);
  }
  // A free-standing schedule week has no roster period. Carry its anchor
  // through adjacent sections so returning to Schedule does not jump to today.
  // An explicitly selected period owns the window instead of a stale anchor.
  const anchor = current.get('anchor');
  if (!validRosterPeriodId(period) && anchor && /^\d{4}-\d{2}-\d{2}$/.test(anchor)) {
    params.set('anchor', anchor);
  } else {
    params.delete('anchor');
  }
  if (path === '/roster/dispatch' && current.get('date')) {
    // An explicit day on a run sheet takes precedence over the saved board window.
    params.set('date', current.get('date')!);
    const explicitWeek = current.get('week');
    if (explicitWeek) params.set('week', explicitWeek);
    else params.delete('week');
    if (!validRosterPeriodId(period)) params.delete('period');
  }
  if (attendance) {
    for (const key of ['date', 'week']) {
      const value = current.get(key);
      if (value) params.set(key, value);
    }
  }
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export type DispatchPreferenceMode = 'RESIDENTIAL' | 'HOME_CARE' | 'MIXED';
const MODE_VIEWS: Record<DispatchPreferenceMode, readonly string[]> = {
  RESIDENTIAL: ['week', 'day', 'workers', 'coverage', 'services', 'departments', 'month'],
  HOME_CARE: ['week', 'day', 'workers', 'clients', 'services', 'departments', 'heatmap', 'month'],
  MIXED: [
    'week',
    'day',
    'workers',
    'clients',
    'coverage',
    'services',
    'departments',
    'heatmap',
    'month',
  ],
};

export function dispatchViewCookieName(mode: DispatchPreferenceMode): string {
  return `statura_roster_view_v2_${mode.toLowerCase()}`;
}

/** Untrusted preferences never bypass mode or pilot gates. */
export function validDispatchCookieView(
  value: string | null | undefined,
  mode: DispatchPreferenceMode,
  advanced = false,
): string | undefined {
  return value && MODE_VIEWS[mode].includes(value) && (value !== 'month' || advanced)
    ? value
    : undefined;
}

export function resolveDispatchViewPreference(
  explicit: string | undefined,
  cookie: string | undefined,
  mode: DispatchPreferenceMode,
  advanced = false,
): string {
  // Even a malformed explicit URL wins over a stored preference, then falls back.
  return (
    validDispatchCookieView(explicit !== undefined ? explicit : cookie, mode, advanced) ??
    (mode === 'RESIDENTIAL' ? 'workers' : 'week')
  );
}

export function writeDispatchViewCookie(view: string, mode: DispatchPreferenceMode): void {
  if (typeof document === 'undefined' || !validDispatchCookieView(view, mode, true)) return;
  document.cookie = `${dispatchViewCookieName(mode)}=${view}; Path=/roster; SameSite=Lax; Max-Age=31536000${window.location.protocol === 'https:' ? '; Secure' : ''}`;
}

/** Legacy home-care/mixed choice applies on the NEXT load, never by navigation. */
export function migrateDispatchViewCookie(mode: DispatchPreferenceMode): void {
  if (mode === 'RESIDENTIAL' || typeof document === 'undefined') return;
  const name = dispatchViewCookieName(mode);
  if (document.cookie.split(';').some((part) => part.trim().startsWith(`${name}=`))) return;
  try {
    const marker = `${name}:migrated`;
    if (window.localStorage.getItem(marker)) return;
    const view = readStoredDispatchWorkspace()?.view;
    if (view && validDispatchCookieView(view, mode, true)) writeDispatchViewCookie(view, mode);
    window.localStorage.setItem(marker, '1');
  } catch {
    // Unavailable storage must never delay the board.
  }
}
